import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import "./MainAnalysis.css";
import {
  DAYS,
  findMissingSalesDaysWithGaps,
  prepareLineSeries,
  preparePieCountByAppAndBranch,
} from "../../../utils/DailyChartData";
import { fetchCompsAPI, fetchDailysAPI } from "../../../utils/api/report";
import { fetchMonthlysAPI } from "../../../utils/api/report";
import { Context } from "../../../context/Context";
import { translations } from "../../../utils/lang";
import { LineChartAnalysis } from "../charts/line/LineChartAnalysis";
import FilterGroup from "../filter/FilterUI";
import { ModeSwitch } from "../filter/ModeSwitch";
import { BASIC_FILTER, statuses } from "../../../utils/helpers";
import { integrationLabel, normalizeIntegrationKey } from "../../../utils/integrations";
import { MissingTable } from "../charts/missingTable/MissingTable";
import { PieChartAnalysis } from "../charts/pie/PieChartAnalysis";
import { TopValue } from "../charts/topValue/TopValue";
import Calendar from "../charts/calendar/Calendar";
import {
  getTopAndLeastBranches,
  prepareMainLineSeries,
  preparePieCompletedByApp,
  summarizeDailyToMonthly,
  summarizeMonthlyData,
} from "../../../utils/MainCharts";
import CombinedBarLineChart from "../charts/barLine/CombinedBarLineChart";
import MonthlySummaryTable from "../charts/circleCompound/CircleCompound";
import { Causes } from "../charts/causes/Causes";
import { BarChartAnalysis } from "../charts/bar/BarChartAnalysis";
import {
  prepareAllTimelines,
  preparePieChartAnalysis,
} from "../../../utils/MonthlyChartData";
import TimeTable from "../charts/timeTable/TimeTable";
import DetailsOverviewTable from "../charts/DetailsOverviewTable/DetailsOverviewTable";
import { Loading } from "../../loading/Loading";
import { getObjectionsAPI } from "../../../utils/api/objection";
import { Button } from "../../ui";
import { fetchStoresAPI } from "../../../utils/api/stores";
import { FEATURE_NAMES, isFeatureEnabled } from "../../../utils/features";
import { defaultRange, isDateInRange } from "../../../utils/analysisWindow";
import useAnalysisWindow from "../../../utils/useAnalysisWindow";
import { objectionMatchesFilters } from "../../../utils/objectionFilter";
import AnalysisRangeBar, { AnalysisWindowStatus } from "../AnalysisRangeBar";

// Rows are grouped, filtered and charted by canonical app key; objections and
// older rows may carry a display name, so every comparison goes through this.
const appKeyOf = (value) => normalizeIntegrationKey(value) || value;
const withCanonicalApp = (rows) =>
  Array.isArray(rows) ? rows.map((row) => ({ ...row, app: appKeyOf(row.app) })) : rows;
// Distinct trimmed values of a row field, in first-seen order. Branch and store options come from the rows' branchName / storeName, which is what the
// filters compare (rows have no `branch` / `store` field).
const uniqueTrimmed = (rows, key) => [
  ...new Set(rows.map((row) => String(row[key] ?? "").trim()).filter(Boolean)),
];
const uniqueAppKeys = (rows) => [
  ...new Set(rows.map((row) => appKeyOf(row.app)).filter(Boolean)),
];

export const MainAnalysis = () => {
  const { lang, storeAccess, handleTokenExpiration } = useContext(Context);
  const [appMode, setAppMode] = useState("amount");
  const [branchMode, setBranchMode] = useState("amount");

  const [isLoading, setIsLoading] = useState(true);
  const [dailyState, setDaily] = useState([]);
  const [monthlyState, setMonthly] = useState([]);
  const [detailsState, setDetails] = useState([]);
  const [objections, setObjections] = useState([]);

  // REPORT_ANALYSIS_WINDOW: bounded period requests instead of the whole history. Off keeps the original flow untouched.
  const windowOn = isFeatureEnabled(FEATURE_NAMES.REPORT_ANALYSIS_WINDOW);
  const [range, setRange] = useState(() => defaultRange());
  const [activeStoreIds, setActiveStoreIds] = useState(null);
  const win = useAnalysisWindow({
    kinds: ["daily", "monthly", "compensation"],
    storeIds: activeStoreIds,
    range,
    enabled: windowOn && Array.isArray(activeStoreIds) && activeStoreIds.length > 0, // no stores selected = no data, never "everything"
  });
  const dailyWin = useMemo(() => withCanonicalApp(win.daily || []), [win.daily]);
  const monthlyWin = useMemo(() => withCanonicalApp(win.monthly || []), [win.monthly]);
  const detailsWin = useMemo(() => withCanonicalApp(win.compensation || []), [win.compensation]);
  const daily = windowOn ? dailyWin : dailyState;
  const monthly = windowOn ? monthlyWin : monthlyState;
  const details = windowOn ? detailsWin : detailsState;
  const [availableStores, setAvailableStores] = useState([]);
  const [defaultStoreIds, setDefaultStoreIds] = useState([]);
  const [filters, setFilters] = useState(BASIC_FILTER);
  const [filtersDaily, setFiltersDaily] = useState(BASIC_FILTER);

  const appsDaily = uniqueAppKeys(daily);
  const storeOptions = useMemo(
    () =>
      availableStores
        .map((store) => store.businessName)
        .filter(Boolean),
    [availableStores],
  );
  const storesDaily = storeOptions.length ? storeOptions : uniqueTrimmed(daily, "storeName");
  const getFilteredBranchesDaily = () => {
    const selectedStores = filtersDaily.store || [];

    if (selectedStores.length === 0) {
      return uniqueTrimmed(daily, "branchName");
    }

    const filteredBranches = new Set(
      daily
        .filter((item) => selectedStores.includes(item.storeName))
        .map((item) => String(item.branchName ?? "").trim())
        .filter(Boolean)
    );

    return Array.from(filteredBranches);
  };
  const monthsDaily = [...new Set(daily.map((d) => d.date.split("-")[1]))];
  const yearsDaily = [...new Set(daily.map((d) => d.date.split("-")[2]))];

  const apps = uniqueAppKeys(monthly);
  const stores = storeOptions.length ? storeOptions : uniqueTrimmed(monthly, "storeName");
  const getFilteredBranches = () => {
    const selectedStores = filters.store || [];

    if (selectedStores.length === 0) {
      return uniqueTrimmed(monthly, "branchName");
    }

    const filteredBranches = new Set(
      monthly
        .filter((item) => selectedStores.includes(item.storeName))
        .map((item) => String(item.branchName ?? "").trim())
        .filter(Boolean)
    );

    return Array.from(filteredBranches);
  };
  const months = [...new Set(monthly.map((d) => d.date.split("-")[0]))];
  const years = [...new Set(monthly.map((d) => d.date.split("-")[1]))];

  const handleFilterChange = (category, values) => {
    setFilters((prev) => ({
      ...prev,
      [category]: values,
    }));
  };
  const handleFilterChangeDaily = (category, values) => {
    setFiltersDaily((prev) => ({
      ...prev,
      [category]: values,
    }));
  };

  const filteredData = useMemo(() => {
    return daily.filter((item) => {
      if (filtersDaily.app.length && !filtersDaily.app.includes(appKeyOf(item.app)))
        return false;
      if (filtersDaily.store.length && !filtersDaily.store.includes(item.storeName))
        return false;
      if (
        filtersDaily.branch.length &&
        !filtersDaily.branch.includes(String(item.branchName ?? "").trim())
      )
        return false;
      if (filtersDaily.status.length) {
        const matched = filtersDaily.status.some((s) => item[s] && item[s] > 0);
        if (!matched) return false;
      }
      if (
        filtersDaily.month.length &&
        !filtersDaily.month.includes(item.date.split("-")[1])
      )
        return false;
      if (
        filtersDaily.year.length &&
        !filtersDaily.year.includes(item.date.split("-")[2])
      )
        return false;
      return true;
    });
  }, [daily, filtersDaily]);

  const detailsFilter = useMemo(() => {
    return details.filter((item) => {
      if (filtersDaily.app.length && !filtersDaily.app.includes(appKeyOf(item.app)))
        return false;
      if (
        filtersDaily.status &&
        (filtersDaily.status.includes(item.orderStatus.toLowerCase()) ||
          (filtersDaily.status.includes("completed") &&
            item.orderStatus === "Delivered"))
      )
        return false;

      if (filtersDaily.store.length && !filtersDaily.store.includes(item.storeName))
        return false;
      if (
        filtersDaily.branch.length &&
        !filtersDaily.branch.includes(String(item.branchName ?? "").trim())
      )
        return false;
      // STATUS FILTER IGNORED HERE
      if (
        filtersDaily.month.length &&
        !filtersDaily.month.includes(item.date.split("-")[1])
      )
        return false;
      if (
        filtersDaily.year.length &&
        !filtersDaily.year.includes(item.date.split("-")[2])
      )
        return false;
      return true;
    });
  }, [details, filtersDaily]);

  const objectionsFilter = useMemo(() => {
    // Objections store `store` / `mainStoreId` / `branch`, not storeName / branchName (see utils/objectionFilter.js).
    // With the window on they are limited to the same period as the report rows they are compared with.
    return objections.filter((item) => {
      if (windowOn && !isDateInRange(item.creationDate, range)) return false;
      return objectionMatchesFilters(item, filtersDaily, availableStores);
    });
  }, [objections, filtersDaily, availableStores, windowOn, range]);

  const filteredMonthly = useMemo(() => {
    return monthly.filter((item) => {
      if (filters.app.length && !filters.app.includes(appKeyOf(item.app))) return false;
      if (filters.store.length && !filters.store.includes(item.storeName))
        return false;
      if (filters.branch.length && !filters.branch.includes(String(item.branchName ?? "").trim()))
        return false;

      // status: monthly usually has no completed/canceled/etc
      // but if you want it applied, tell me and I'll add.

      if (
        filters.month.length &&
        !filters.month.includes(item.date.split("-")[0])
      )
        return false;
      if (
        filters.year.length &&
        !filters.year.includes(item.date.split("-")[1])
      )
        return false;

      return true;
    });
  }, [monthly, filters]);

  const appSeries = prepareLineSeries(filteredData, "app", appMode, 6, lang);
  const branchSeries = prepareLineSeries(filteredData, "branch", branchMode, 8);
  const { barSeries, lineSeries } = prepareMainLineSeries(filteredData, lang);

  const {
    pieDataApp,
    pieDataBranch,
    totalApp,
    amountCountAvgByApp,
    amountCountAvgByBranch,
  } = preparePieCountByAppAndBranch(detailsFilter, lang, 6);

  const missing = findMissingSalesDaysWithGaps(filteredData);
  const { topBranches, leastBranches } = getTopAndLeastBranches(
    filteredMonthly,
    6
  );
  const { appPieData, totalCompleted,amountCountAvgByAppMonthly } = preparePieCompletedByApp(
    filteredMonthly,
    lang
  );

  const timeLines = prepareAllTimelines(filteredMonthly, lang);

  const fetchDaily = async (storeIds) => {
    try {
      const token = localStorage.getItem("auth-token");
      if (token) {
        const daily = await fetchDailysAPI(token, storeIds);
        if (daily) {
          setDaily(withCanonicalApp(daily));
        }
      } else {
        handleTokenExpiration();
      }
    } catch (error) {
      console.error("Fetching failed daily:", error);
    }
  };
  const fetchObjections = async (storeIds) => {
    try {
      const token = localStorage.getItem("auth-token");
      if (token) {
        const objectionsData = await getObjectionsAPI(token, storeIds);
        if (objectionsData) {
          setObjections(objectionsData);
        }
      } else {
        handleTokenExpiration();
      }
    } catch (error) {
      console.error("Fetching failed daily:", error);
    }
  };
  const fetchDetails = async (storeIds) => {
    try {
      const token = localStorage.getItem("auth-token");
      if (token) {
        const details = await fetchCompsAPI(token, storeIds);
        if (details) {
          setDetails(withCanonicalApp(details));
        }
      } else {
        handleTokenExpiration();
      }
    } catch (error) {
      console.error("Fetching failed details:", error);
    }
  };

  const fetchMonthly = async (storeIds) => {
    try {
      const token = localStorage.getItem("auth-token");
      if (token) {
        const monthly = await fetchMonthlysAPI(token, storeIds);
        if (monthly) {
          setMonthly(withCanonicalApp(monthly));
        } else {
          handleTokenExpiration();
        }
      } else {
        handleTokenExpiration();
      }
    } catch (error) {
      console.error("Fetching failed monthly:", error);
    }
  };

  // Window mode: the report rows come from useAnalysisWindow for `activeStoreIds`; only the objections are fetched here (latest answer wins).
  const objectionsRequest = useRef(0);
  const selectStores = async (storeIds) => {
    const request = ++objectionsRequest.current;
    setActiveStoreIds(storeIds);
    if (!Array.isArray(storeIds) || storeIds.length === 0) {
      setObjections([]);
      return;
    }
    const token = localStorage.getItem("auth-token");
    if (!token) {
      handleTokenExpiration();
      return;
    }
    try {
      const objectionsData = await getObjectionsAPI(token, storeIds);
      if (request === objectionsRequest.current) setObjections(objectionsData || []);
    } catch (error) {
      console.error("Fetching failed objections:", error);
    }
  };

  const fetchAll = async (storeIds) => {
    if (windowOn) {
      setIsLoading(false);
      return selectStores(storeIds);
    }
    if (!Array.isArray(storeIds) || storeIds.length === 0) {
      setDaily([]);
      setMonthly([]);
      setDetails([]);
      setObjections([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    await Promise.all([
      fetchDaily(storeIds),
      fetchMonthly(storeIds),
      fetchDetails(storeIds),
      fetchObjections(storeIds),
    ]);
    setIsLoading(false);
  };

  const getStoreIdsByNames = (storeNames = []) =>
    availableStores
      .filter((store) => storeNames.includes(store.businessName))
      .map((store) => store.id);

  const handleStoreFilterChange = async (values, filterType) => {
    const selectedIds = values.length
      ? getStoreIdsByNames(values)
      : defaultStoreIds;

    if (filterType === "daily") {
      handleFilterChangeDaily("store", values);
    } else {
      handleFilterChange("store", values);
    }

    await fetchAll(selectedIds);
  };

  useEffect(() => {
    const initializeAnalysis = async () => {
      const token = localStorage.getItem("auth-token");
      if (!token) {
        handleTokenExpiration();
        return;
      }

      try {
        const storesData = await fetchStoresAPI(token, storeAccess || []);
        const accessibleStores = Array.isArray(storesData) ? storesData : [];
        const initialStoreIds =
          accessibleStores.length > 5
            ? accessibleStores.slice(0, 5).map((store) => store.id)
            : accessibleStores.map((store) => store.id);

        setAvailableStores(accessibleStores);
        setDefaultStoreIds(initialStoreIds);
        await fetchAll(initialStoreIds);
      } catch (error) {
        console.error("Fetching failed stores:", error);
        setIsLoading(false);
      }
    };

    initializeAnalysis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClearFilters = async () => {
    setFilters(BASIC_FILTER);
    await fetchAll(defaultStoreIds);
  };
  const handleClearFiltersDaily = async () => {
    setFiltersDaily(BASIC_FILTER);
    await fetchAll(defaultStoreIds);
  };

  const summarizedMValue = summarizeMonthlyData(filteredMonthly);
  const summarizedDValue = summarizeDailyToMonthly(filteredData);
  const { serviceChargePie, efficiencyPie } = preparePieChartAnalysis(
    filteredMonthly,
    lang
  );

  if (isLoading) {
    return <Loading />;
  }

  const rangeBar = windowOn && (
    <AnalysisRangeBar lang={lang} range={range} onChange={setRange} window={win.windows.daily || win.windows.monthly} />
  );
  const noRows = !daily.length && !monthly.length && !details.length;
  if (windowOn && (win.loading || win.error)) {
    return (
      <div className="main-analysis-content">
        {rangeBar}
        <AnalysisWindowStatus lang={lang} loading={win.loading} error={win.error} onRetry={win.reload} />
        {win.loading && <Loading />}
      </div>
    );
  }

  return (
    <div>
      <div className="main-analysis-content">
        {rangeBar}
        {windowOn && <AnalysisWindowStatus lang={lang} empty={noRows} />}
        <div className="filters">
          <FilterGroup
            label="App"
            options={appsDaily}
            getLabel={(app) => integrationLabel(app, lang)}
            selected={filtersDaily.app}
            onChange={(vals) => handleFilterChangeDaily("app", vals)}
          />
          <FilterGroup
            label="Store"
            options={storesDaily}
            selected={filtersDaily.store}
            onChange={(vals) => handleStoreFilterChange(vals, "daily")}
          />
          <FilterGroup
            label="Branch"
            options={getFilteredBranchesDaily()}
            selected={filtersDaily.branch}
            onChange={(vals) => handleFilterChangeDaily("branch", vals)}
          />
          <FilterGroup
            label="Status"
            options={statuses}
            selected={filtersDaily.status}
            onChange={(vals) => handleFilterChangeDaily("status", vals)}
          />
          <FilterGroup
            label="Month"
            options={monthsDaily}
            selected={filtersDaily.month}
            onChange={(vals) => handleFilterChangeDaily("month", vals)}
          />
          <FilterGroup
            label="Year"
            options={yearsDaily}
            selected={filtersDaily.year}
            onChange={(vals) => handleFilterChangeDaily("year", vals)}
          />

          <Button
           variant="danger"
              appearance="soft"
              size="sm"
            className="clear-filters-btn"
            onClick={handleClearFiltersDaily}
          >
            {translations[lang].reports.details.inputTitles[6]}
          </Button>
        </div>

        <div className="line-main-analysis-content-top">
          <div className="main-analysis-top-col">
            {filteredData && (
              <div style={{ position: "relative" }}>
                <PieChartAnalysis
                  title={translations[lang].dailyAnalysis.pieTitle[0]}
                  height={250}
                  width={250}
                  series={pieDataApp}
                  data={amountCountAvgByApp}
                  centerValue={totalApp}
                  hideLegend={true}
                />
              </div>
            )}

            {filteredData && (
              <div style={{ position: "relative" }}>
                <PieChartAnalysis
                  title={translations[lang].dailyAnalysis.pieTitle[1]}
                  height={250}
                  width={250}
                  series={pieDataBranch}
                  data={amountCountAvgByBranch}
                  innerRadius={2 - 0}
                  hideLegend={true}
                />
              </div>
            )}
          </div>
          <div className="main-analysis-top-col">
            <CombinedBarLineChart
              title="Analysis"
              xData={DAYS}
              xLabel={translations[lang].dailyAnalysis.date[0]}
              height={400}
              yAxis={[
                {
                  id: "amount",
                  position: "right",
                  width: 65,
                  label: translations[lang].dailyAnalysis.amount,
                },
                {
                  id: "count",
                  position: "left",
                  width: 65,
                  label: translations[lang].dailyAnalysis.count,
                },
              ]}
              lineSeries={lineSeries.map((s) => ({ ...s, yAxisId: "count" }))}
              barSeries={barSeries.map((s) => ({ ...s, yAxisId: "amount" }))}
            />
            <div className="main-analysis-top-row">
              <TimeTable details={detailsFilter} />
            </div>
          </div>
        </div>
        <div className="line-main-analysis-content-grid">
          <Calendar data={filteredData} />
          <div className="main-analysis-top-col">
            <DetailsOverviewTable details={detailsFilter} missing={missing} objections={objectionsFilter} />
            <Causes details={detailsFilter} />
          </div>

          <LineChartAnalysis
            xAxis={[
              {
                data: DAYS,
                scaleType: "band",
                label: translations[lang].dailyAnalysis.date[2],
              },
            ]}
            series={appSeries}
            height={400}
            width={650}
            header={
              <ModeSwitch
                mode={appMode}
                onChange={setAppMode}
                labels={{
                  amount: translations[lang].dailyAnalysis.amount,
                  count: translations[lang].dailyAnalysis.count,
                }}
              />
            }
          />
          <LineChartAnalysis
            xAxis={[
              {
                data: DAYS,
                scaleType: "point",
                label: translations[lang].dailyAnalysis.date[3],
              },
            ]}
            series={branchSeries}
            height={400}
            width={650}
            header={
              <ModeSwitch
                mode={branchMode}
                onChange={setBranchMode}
                labels={{
                  amount: translations[lang].dailyAnalysis.amount,
                  count: translations[lang].dailyAnalysis.count,
                }}
              />
            }
          />
        </div>
        <MissingTable missing={missing} />

        {/* monthly */}

        <div className="filters">
          <FilterGroup
            label="App"
            options={apps}
            getLabel={(app) => integrationLabel(app, lang)}
            selected={filters.app}
            onChange={(vals) => handleFilterChange("app", vals)}
          />
          <FilterGroup
            label="Store"
            options={stores}
            selected={filters.store}
            onChange={(vals) => handleStoreFilterChange(vals, "monthly")}
          />
          <FilterGroup
            label="Branch"
            options={getFilteredBranches()}
            selected={filters.branch}
            onChange={(vals) => handleFilterChange("branch", vals)}
          />
          <FilterGroup
            label="Month"
            options={months}
            selected={filters.month}
            onChange={(vals) => handleFilterChange("month", vals)}
          />
          <FilterGroup
            label="Year"
            options={years}
            selected={filters.year}
            onChange={(vals) => handleFilterChange("year", vals)}
          />

          <Button  variant="danger"
              appearance="soft"
              size="sm" className="clear-filters-btn" onClick={handleClearFilters}>
            {translations[lang].reports.details.inputTitles[6]}
          </Button>
        </div>

        <div className="line-main-analysis-content-top">
          <div className="main-analysis-top-col">
            {filteredData && (
              <div style={{ position: "relative" }}>
                <BarChartAnalysis
                  title={translations[lang].Analysis.chartTitles[14]}
                  height={300}
                  yAxis={[
                    {
                      data: efficiencyPie.map((item) => item.label),
                      scaleType: "band",
                    },
                  ]}
                  layout={"horizontal"}
                  series={[
                    {
                      data: efficiencyPie.map((item) => item.value),
                      label: translations[lang].Analysis.chartTitles[14],
                      color: "#ac24eb",
                      valueFormatter: (value) => `${value}%`,
                    },
                  ]}
                />
              </div>
            )}

            {filteredData && (
              <div style={{ position: "relative" }}>
                <BarChartAnalysis
                  title={translations[lang].Analysis.chartTitles[13]}
                  height={300}
                  yAxis={[
                    {
                      data: serviceChargePie.map((item) => item.label),
                      scaleType: "band",
                    },
                  ]}
                  layout={"horizontal"}
                  series={[
                    {
                      data: serviceChargePie.map((item) => item.value),
                      label: translations[lang].Analysis.chartTitles[13],
                      color: "#ac24eb",
                      valueFormatter: (value) => `${value}%`,
                    },
                  ]}
                />
              </div>
            )}
          </div>
          <div className="main-analysis-top-col">
            <div className="main-analysis-top-row">
              {filteredData && (
                <div style={{ position: "relative" }}>
                  <PieChartAnalysis
                    title={translations[lang].dailyAnalysis.pieTitle[2]}
                    series={appPieData}
                    height={250}
                    width={250}
                    data={amountCountAvgByAppMonthly}
                    centerValue={totalCompleted}
                    isAmount={true}
                    hideLegend={true}
                  />
                </div>
              )}
              <TopValue data={topBranches} title="Top Branches" />
              <TopValue data={leastBranches} title="Least Branches" />
            </div>

            <CombinedBarLineChart
              title={translations[lang].Analysis.chartTitles[4]}
              xData={timeLines.overall.months}
              xLabel={translations[lang].Analysis.chartTitles[4]}
              yAxis={[
                {
                  id: "amount",
                  position: "right",
                  width: 65,
                  label: translations[lang].dailyAnalysis.amount,
                },
                {
                  id: "count",
                  position: "left",
                  width: 65,
                  label: translations[lang].dailyAnalysis.count,
                },
              ]}
              lineSeries={timeLines.overall.lineSeries?.map((s) => ({
                ...s,
                yAxisId: "count",
              }))}
              barSeries={timeLines.overall.barSeries?.map((s) => ({
                ...s,
                yAxisId: "amount",
              }))}
            />
          </div>
        </div>
        <MonthlySummaryTable data={summarizedMValue} daily={summarizedDValue} />
      </div>
    </div>
  );
};
